/**
 * Sentiment Dashboard Component
 *
 * Displays comprehensive sentiment analysis from interview transcripts:
 * 1. Emotion Distribution: token-colored bars per detected emotion
 * 2. FAIR Heatmap: numeric persona × pillar sentiment on the --tri-sent-* ramp
 * 3. Key Insights: biggest pain point / biggest win (derived from real data)
 * 4. Supporting Quotes: top positive/negative quotes with persona + pillar
 *
 * v2.0 redesign: emoji retired (Epic F precedent), cream/teal token system,
 * cells render numeric scores like OrgSentimentHeatmap. Props widened, not
 * broken: emotion_distribution and the insight scalars are optional so legacy
 * call sites passing the full server shape still compile; counts and the
 * fabricated intensity/consensus values are no longer rendered anywhere.
 *
 * Sprint 7 - Interview Enhancement · reworked v2.0
 */

import React from 'react';
import {
  formatScore,
  getPersonaAbbrev,
  getSentimentColor,
  getTextColor,
} from './sentiment/sentimentScale';
import '../styles/sentiment-dashboard.css';

interface EmotionScore {
  emotion: string;
  percentage: number;
  count?: number;
}

interface SentimentQuote {
  text: string;
  sentiment: number;
  persona?: string;
}

interface FairPillarSentiment {
  overall_sentiment: number;
  by_persona: Record<string, number>; // Persona-based sentiment scores
  quotes: SentimentQuote[];
}

interface SentimentAnalysis {
  emotions: {
    overall_emotion: string;
    emotion_scores: Record<string, number>;
    /** Optional — when absent, rows derive from emotion_scores directly. */
    emotion_distribution?: EmotionScore[];
  };
  fair_heatmap: {
    by_pillar: Record<string, FairPillarSentiment>;
  };
  insights: {
    biggest_pain_point: string;
    biggest_win: string;
    /** Accepted for backward compatibility; never rendered. */
    emotional_intensity?: number;
    /** Accepted for backward compatibility; never rendered. */
    consensus_level?: number;
  };
  metadata: {
    total_statements: number;
    analyzed_at: string;
    model: string;
  };
}

interface SentimentDashboardProps {
  sentiment: SentimentAnalysis;
}

const FAIR_PILLARS = ['Findable', 'Accessible', 'Interoperable', 'Reusable'] as const;

/**
 * Emotion → fill token. Negative-affect emotions sit on the warm end of the
 * sentiment ramp, positive-affect on the green end.
 */
const EMOTION_FILL: Record<string, string> = {
  frustration: 'var(--tri-sent-n3)',
  anxiety: 'var(--tri-sent-n2)',
  confusion: 'var(--tri-sent-n1)',
  confidence: 'var(--tri-sent-p2)',
  joy: 'var(--tri-sent-p3)',
};

function getEmotionFill(emotion: string): string {
  return EMOTION_FILL[emotion.toLowerCase()] ?? 'var(--tri-sent-0)';
}

/**
 * Emotion rows: prefer the provided distribution; otherwise derive from
 * emotion_scores (score in [0,1] → percentage). Sorted strongest-first.
 */
function getEmotionRows(emotions: SentimentAnalysis['emotions']): EmotionScore[] {
  const rows: EmotionScore[] =
    emotions.emotion_distribution && emotions.emotion_distribution.length > 0
      ? emotions.emotion_distribution.map(({ emotion, percentage }) => ({ emotion, percentage }))
      : Object.entries(emotions.emotion_scores).map(([emotion, score]) => ({
          emotion,
          percentage: Math.round(score * 100),
        }));
  return rows.sort((a, b) => b.percentage - a.percentage);
}

/**
 * Extract unique personas from the heatmap data
 */
function getUniquePersonas(fair_heatmap: SentimentAnalysis['fair_heatmap']): string[] {
  const personaSet = new Set<string>();
  Object.values(fair_heatmap.by_pillar).forEach(pillarData => {
    if (pillarData.by_persona) {
      Object.keys(pillarData.by_persona).forEach(persona => personaSet.add(persona));
    }
  });
  return Array.from(personaSet).sort();
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function SentimentDashboard({ sentiment }: SentimentDashboardProps) {
  const { emotions, fair_heatmap, insights } = sentiment;

  const emotionRows = getEmotionRows(emotions);
  const personas = getUniquePersonas(fair_heatmap);

  // Extract top 3 negative and positive quotes across all pillars using
  // individual per-quote sentiment scores (not pillar-level scores).
  const allQuotes: Array<{ quote: string; pillar: string; score: number; persona?: string }> = [];
  Object.entries(fair_heatmap.by_pillar).forEach(([pillar, data]) => {
    data.quotes.forEach(quoteObj => {
      allQuotes.push({
        quote: quoteObj.text,
        pillar,
        score: quoteObj.sentiment,
        persona: quoteObj.persona,
      });
    });
  });

  const negativeQuotes = allQuotes
    .filter(q => q.score < 0)
    .sort((a, b) => a.score - b.score) // Most negative first
    .slice(0, 3);

  const positiveQuotes = allQuotes
    .filter(q => q.score > 0)
    .sort((a, b) => b.score - a.score) // Most positive first
    .slice(0, 3);

  const painSentiment = fair_heatmap.by_pillar[insights.biggest_pain_point]?.overall_sentiment ?? 0;
  const winSentiment = fair_heatmap.by_pillar[insights.biggest_win]?.overall_sentiment ?? 0;

  return (
    <div className="sentiment-dashboard">
      <div className="sentiment-dashboard__header">
        <h3>Sentiment Analysis</h3>
        <div className="sentiment-dashboard__meta">
          <span>{sentiment.metadata.total_statements} statements analyzed</span>
          {emotions.overall_emotion && (
            <span className="sentiment-dashboard__emotion-chip">
              Overall: {capitalize(emotions.overall_emotion)}
            </span>
          )}
        </div>
      </div>

      <div className="sentiment-dashboard__grid">
        {/* Left Column: Emotion Distribution */}
        <div className="sentiment-dashboard__section sentiment-dashboard__emotions">
          <h4>Emotion Distribution</h4>
          <div className="emotion-bars">
            {emotionRows.map(({ emotion, percentage }) => (
              <div key={emotion} className="emotion-bar">
                <div className="emotion-bar__label">{capitalize(emotion)}</div>
                <div className="emotion-bar__track">
                  <div
                    className="emotion-bar__fill"
                    style={{
                      width: `${Math.max(0, Math.min(100, percentage))}%`,
                      backgroundColor: getEmotionFill(emotion),
                    }}
                  />
                </div>
                <div className="emotion-bar__value">{percentage}%</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column: FAIR Heatmap */}
        <div className="sentiment-dashboard__section sentiment-dashboard__heatmap">
          <h4>FAIR Sentiment by Persona</h4>
          <div className="fair-heatmap">
            <div className="fair-heatmap__row fair-heatmap__row--header">
              <div className="fair-heatmap__cell fair-heatmap__cell--corner">Persona</div>
              {FAIR_PILLARS.map(pillar => (
                <div key={pillar} className="fair-heatmap__cell fair-heatmap__cell--header">
                  <span className="fair-heatmap__pillar-letter">{pillar[0]}</span>
                  <span className="fair-heatmap__pillar-name">{pillar}</span>
                </div>
              ))}
            </div>

            {personas.map(persona => (
              <div key={persona} className="fair-heatmap__row">
                <div className="fair-heatmap__cell fair-heatmap__cell--persona" title={persona}>
                  <span className="fair-heatmap__persona-abbrev">{getPersonaAbbrev(persona)}</span>
                  <span className="fair-heatmap__persona-full">{persona}</span>
                </div>
                {FAIR_PILLARS.map(pillar => {
                  const score = fair_heatmap.by_pillar[pillar]?.by_persona?.[persona];
                  const hasData = score !== undefined;
                  return (
                    <div
                      key={pillar}
                      className="fair-heatmap__cell fair-heatmap__cell--data"
                      style={{
                        backgroundColor: hasData ? getSentimentColor(score) : 'var(--tri-track)',
                        color: hasData ? getTextColor(score) : 'var(--tri-slate)',
                      }}
                      title={`${pillar} (${persona}): ${hasData ? formatScore(score) : 'no data'}`}
                    >
                      {hasData ? formatScore(score) : '—'}
                    </div>
                  );
                })}
              </div>
            ))}

            <div className="fair-heatmap__row fair-heatmap__row--footer">
              <div className="fair-heatmap__cell fair-heatmap__cell--corner">Overall</div>
              {FAIR_PILLARS.map(pillar => {
                const pillarData = fair_heatmap.by_pillar[pillar];
                const score = pillarData?.overall_sentiment;
                const hasData = score !== undefined;
                return (
                  <div
                    key={pillar}
                    className="fair-heatmap__cell fair-heatmap__cell--data fair-heatmap__cell--average"
                    style={{
                      backgroundColor: hasData ? getSentimentColor(score) : 'var(--tri-track)',
                      color: hasData ? getTextColor(score) : 'var(--tri-slate)',
                    }}
                  >
                    {hasData ? formatScore(score) : '—'}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="fair-heatmap__legend">
            <span className="fair-heatmap__legend-label">Sentiment:</span>
            <div className="fair-heatmap__legend-scale">
              <span style={{ backgroundColor: 'var(--tri-sent-n3)' }}>-1.0</span>
              <span style={{ backgroundColor: 'var(--tri-sent-n2)' }}>-0.5</span>
              <span style={{ backgroundColor: 'var(--tri-sent-n1)' }}>-0.2</span>
              <span style={{ backgroundColor: 'var(--tri-sent-0)' }}>0</span>
              <span style={{ backgroundColor: 'var(--tri-sent-p1)' }}>+0.2</span>
              <span style={{ backgroundColor: 'var(--tri-sent-p2)' }}>+0.5</span>
              <span style={{ backgroundColor: 'var(--tri-sent-p3)' }}>+1.0</span>
            </div>
          </div>

          <div className="fair-heatmap__insights">
            <div className="fair-heatmap__insight fair-heatmap__insight--pain">
              <span className="fair-heatmap__insight-label">Biggest Pain</span>
              <span className="fair-heatmap__insight-value">
                {insights.biggest_pain_point} ({formatScore(painSentiment)})
              </span>
            </div>
            <div className="fair-heatmap__insight fair-heatmap__insight--win">
              <span className="fair-heatmap__insight-label">Biggest Win</span>
              <span className="fair-heatmap__insight-value">
                {insights.biggest_win} ({formatScore(winSentiment)})
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section: Supporting Quotes */}
      <div className="sentiment-dashboard__section sentiment-dashboard__quotes">
        <h4>Supporting Quotes</h4>
        <div className="sentiment-quotes">
          {negativeQuotes.length > 0 && (
            <div className="sentiment-quotes__group">
              <h5 className="sentiment-quotes__group-title sentiment-quotes__group-title--negative">
                Most Negative
              </h5>
              {negativeQuotes.map((item, index) => (
                <div key={index} className="sentiment-quote sentiment-quote--negative">
                  <div className="sentiment-quote__text">&quot;{item.quote}&quot;</div>
                  <div className="sentiment-quote__meta">
                    {item.persona ? `${item.persona} · ` : ''}
                    {item.pillar} ({formatScore(item.score)})
                  </div>
                </div>
              ))}
            </div>
          )}

          {positiveQuotes.length > 0 && (
            <div className="sentiment-quotes__group">
              <h5 className="sentiment-quotes__group-title sentiment-quotes__group-title--positive">
                Most Positive
              </h5>
              {positiveQuotes.map((item, index) => (
                <div key={index} className="sentiment-quote sentiment-quote--positive">
                  <div className="sentiment-quote__text">&quot;{item.quote}&quot;</div>
                  <div className="sentiment-quote__meta">
                    {item.persona ? `${item.persona} · ` : ''}
                    {item.pillar} ({formatScore(item.score)})
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
